// Auth state — single source of truth for "who is the current user".
// Backed by /api/v1/me + Sanctum cookies; refreshed once on app boot.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { ApiError, me, type Me } from '../lib/api';

interface AuthValue {
  user: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setUser(await me());
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 419)) {
        setUser(null);
      } else {
        // Network failure, 500, etc. Leave user null so the SPA shows the
        // unauthenticated UI rather than spinning forever.
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return <AuthCtx.Provider value={{ user, loading, refresh }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
